/**
 * Payoff of a held Trade, never a template or a hypothetical entry.
 * BookDrawer owns the book; the dialog shares that same authoritative snapshot.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { BarChart3, Info, Table2, X } from 'lucide-react'
import { chanceOfProfit, expiryCode, payoffShape, valueAt } from '@apna/shared'
import { cn } from '@/lib/utils'
import { PayoffChart } from '@/components/payoff/PayoffChart'
import { compactInr, inr } from './quantity'
import { payoffTrades, profitableInside } from './heldPayoff'
import { PayoffTradePicker } from './PayoffTradePicker'
import { useHeldTradePricing } from './useHeldTradePricing'
import { Stat, Segmented, Slider, ZoomControl } from './PayoffControls'
import {
  FIT,
  halfPctOf,
  zoomWindow,
  type PayoffZoom,
} from '@/components/payoff/payoffZoom'
import { PayoffHeatGrid } from './PayoffHeatGrid'
import type { OpenBookState, OpenBookTrade } from './useOpenBook'
import type { PayoffLeg } from '@apna/shared'

/** Stable empty, so the memos above the `held` guard do not churn. */
const NO_LEGS: readonly PayoffLeg[] = []

export interface PayoffDockProps {
  open: boolean
  onClose: () => void
  book: OpenBookState
  onRefresh: () => void
}

export function PayoffDock({ open, onClose, book, onRefresh }: PayoffDockProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next) onClose() }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-black/40" data-testid="payoff-dock-overlay" />
        <DialogPrimitive.Content className="fixed inset-x-0 bottom-0 z-[71] flex h-[84dvh] max-h-[calc(100dvh-16px)] flex-col overflow-hidden rounded-t-xl border-x border-t border-border bg-card shadow-2xl outline-none" data-testid="payoff-dock">
          {open && <TradeSelection book={book} onRefresh={onRefresh} />}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function TradeSelection({ book, onRefresh }: Pick<PayoffDockProps, 'book' | 'onRefresh'>) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const trades = book.kind === 'READY' ? payoffTrades(book.trades) : []
  const selected = selectedId === null ? trades[0] : trades.find((t) => t.tradeId === selectedId)
  const firstId = trades[0]?.tradeId
  useEffect(() => {
    if (selectedId === null && firstId) setSelectedId(firstId)
  }, [selectedId, firstId])
  return (
    <>
      <header className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-2.5" data-testid="payoff-header">
        <DialogPrimitive.Title className="shrink-0 text-sm font-semibold">Payoff Chart</DialogPrimitive.Title>
        <DialogPrimitive.Description className="sr-only">Your held trades at recorded average prices. Excludes realised P&amp;L and charges.</DialogPrimitive.Description>
        <span title="Open legs at recorded average prices. Excludes realised P&L and charges. Model curves use this trade’s own expiry and IV." className="hidden shrink-0 text-muted-foreground sm:inline-flex"><Info size={13} /></span>
        <div className="min-w-0 flex-1 sm:max-w-[520px]">
          <PayoffTradePicker trades={trades} selected={selected} onSelect={setSelectedId} />
        </div>
        <span className="hidden shrink-0 rounded bg-secondary/60 px-2 py-1 text-[10px] tabular-nums text-muted-foreground sm:inline">{trades.length} open · all accounts</span>
        <DialogPrimitive.Close aria-label="Close payoff chart" className="ml-auto shrink-0 rounded p-1.5 text-muted-foreground hover:bg-secondary"><X size={16} /></DialogPrimitive.Close>
      </header>
      {book.kind !== 'READY' ? (
        <div role="status" className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center text-sm text-muted-foreground">
          {book.kind === 'LOADING' ? 'Loading your open trades…' : 'Open trades could not be read: ' + book.why}
          {book.kind === 'FAILED' && <button type="button" className="underline" onClick={onRefresh}>Retry open trades</button>}
        </div>
      ) : selected ? <SelectedTrade key={selected.tradeId} trade={selected} /> : (
        <div role="status" data-testid="payoff-dock-empty" className="flex flex-1 items-center justify-center px-6 text-center text-sm text-muted-foreground">
          {trades.length === 0 ? 'No held trades to chart. Open a position to view its payoff.' : 'The selected trade is no longer open. Choose another trade above.'}
        </div>
      )}
    </>
  )
}

function SelectedTrade({ trade }: { trade: OpenBookTrade }) {
  const [view, setView] = useState<'GRAPH' | 'TABLE'>('GRAPH')
  const [asPercent, setAsPercent] = useState(false)
  const [ivMultiplier, setIvMultiplier] = useState(1)
  /*
   * FIT by default — 9.46. A fixed percentage was the wrong default in the
   * obvious way: at ±2% a Bull Put Spread's max-profit plateau ran off the
   * left edge, so the chart could not show where the position stops making
   * money, which is the one thing it exists to show. Fit covers every
   * strike plus a margin, so the whole structure is always in frame and the
   * reader zooms IN from there rather than hunting for the shape.
   */
  const [zoom, setZoom] = useState<PayoffZoom>(FIT)
  const { held, surface, spot, why } = useHeldTradePricing(trade, ivMultiplier)
  const body = useRef<HTMLDivElement | null>(null)
  const [chartHeight, setChartHeight] = useState(300)
  useEffect(() => {
    const el = body.current
    if (!el || view !== 'GRAPH' || typeof ResizeObserver === 'undefined') return
    const measure = () => setChartHeight(Math.max(60, Math.floor(el.clientHeight - 16)))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [view, held.kind])

  /*
   * ── MEMOISED ABOVE THE EARLY RETURN — 9.49 ─────────────────────────────
   *
   * Both are read by `PayoffChart`'s own `useMemo`s, so a fresh object or
   * closure per render defeats them: the chart re-sampled 160 points and
   * re-valued the T+0 curve on every tick of the underlying, for inputs
   * that had not moved. They sit here rather than after the `held` guard
   * because hooks may not follow a conditional return.
   */
  const legs = held.kind === 'READY' ? held.legs : NO_LEGS
  const window = useMemo(() => zoomWindow(zoom, spot, legs), [zoom, spot, legs])
  const t0 = useMemo(
    () => (surface ? (at: number) => valueAt(surface, at, surface.years) : null),
    [surface],
  )

  if (held.kind !== 'READY') return <div role="status" className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">{held.why}</div>
  const shape = payoffShape(legs)
  const credit = legs.reduce((n, l) => n - l.avgPrice * l.netQty, 0)
  const today = surface ? valueAt(surface, spot, surface.years) : null
  const pop = surface ? chanceOfProfit({
    breakevens: shape.breakevens, spot, iv: surface.legs[0]!.iv * ivMultiplier,
    years: surface.years, insidePays: profitableInside(legs, shape.breakevens),
  }) : null
  /*
   * ONE window, computed once, handed to BOTH views — 9.46. The graph took
   * a `window` and the table took a `rangePct`, which is two derivations of
   * "the displayed range" on two views the reader switches between
   * expecting to see the same thing.
   */
  const halfPct = halfPctOf(window, spot)
  const risk = shape.maxLoss.kind === 'FINITE' ? Math.abs(shape.maxLoss.value) : null
  const assumption = ivMultiplier !== 1 ? '×' + ivMultiplier.toFixed(2) : undefined
  return (
    <>
      <div className="grid shrink-0 grid-cols-2 divide-x divide-border border-b border-border sm:grid-cols-3 lg:grid-cols-6">
        <Stat label={credit >= 0 ? 'Entry credit' : 'Entry debit'} value={inr(Math.abs(credit))} testId="payoff-credit" hint="Recorded average prices × remaining signed quantities, not current quotes or the original template size." />
        <Stat label="Model P&L today" value={today === null ? '—' : (today < 0 ? '-' : '+') + '₹' + compactInr(Math.abs(today))} tone={today !== null && today < 0 ? 'text-destructive' : 'text-success'} testId="payoff-today" assumption={assumption} />
        <Stat label="Max profit" value={shape.maxProfit.kind === 'FINITE' ? '₹' + compactInr(shape.maxProfit.value) : 'no limit'} tone="text-success" testId="payoff-maxp" />
        <Stat label="Max loss" value={risk === null ? 'no limit' : '₹' + compactInr(risk)} tone="text-destructive" testId="payoff-maxl" />
        <Stat label="Breakevens" value={shape.breakevens.length ? shape.breakevens.map((b) => Math.round(b).toLocaleString('en-IN')).join(' · ') : '—'} testId="payoff-be" hint="Expiry payoff of remaining open legs; excludes realised P&L and charges." />
        <Stat label="Chance of profit" value={pop === null ? '—' : pop.toFixed(0) + '%'} hint="Model-implied probability, not a forecast. Unavailable for unsupported profit regions or missing prices." testId="payoff-pop" assumption={assumption} />
      </div>
      {!surface && <p role="status" className="shrink-0 border-b border-border px-5 py-2 text-[11px] text-muted-foreground">{why} Today’s curve and time table are unavailable.</p>}
      <div ref={body} data-testid="payoff-plot-area" className={cn('min-h-0 flex-1 px-3 py-2', view === 'GRAPH' ? 'overflow-hidden' : 'overflow-auto')}>
        {view === 'GRAPH' ? <PayoffChart legs={legs} spot={spot > 0 ? spot : null} window={spot > 0 ? window : undefined} t0={t0} height={chartHeight} compact className="h-full" /> :
          surface && halfPct !== null ? <PayoffHeatGrid surface={surface} rangePct={halfPct} asPercent={asPercent} risk={risk} spot={spot} /> :
          <p className="p-6 text-center text-sm text-muted-foreground">A time table needs this trade’s current quotes and expiry forward. The expiry graph is still available.</p>}
      </div>
      <footer className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-t border-border px-4 py-2">
        <Segmented value={view} onChange={setView} options={[{ value: 'GRAPH' as const, label: 'Graph', icon: BarChart3 }, { value: 'TABLE' as const, label: 'Table', icon: Table2 }]} testId="payoff-view" />
        <Segmented value={asPercent ? 'PCT' : 'INR'} onChange={(v) => setAsPercent(v === 'PCT')} options={[{ value: 'INR' as const, label: '₹ P&L' }, { value: 'PCT' as const, label: risk !== null && risk > 0 ? '% of max risk' : '% of grid scale' }]} testId="payoff-value-mode" />
        <Slider label="IV" value={ivMultiplier} display={'×' + ivMultiplier.toFixed(2)} min={0.5} max={2} step={0.05} onChange={setIvMultiplier} testId="payoff-iv" />
        <ZoomControl zoom={zoom} resolvedPct={halfPct} onChange={setZoom} testId="payoff-range" />
        <div className="ml-auto flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="hidden items-center gap-1.5 lg:inline-flex"><span className="w-4 border-t-2 border-success" />Expiry</span>
          {surface && <span className="hidden items-center gap-1.5 lg:inline-flex"><span className="w-4 border-t border-dashed border-primary" />Today</span>}
          <span data-testid="payoff-position-summary">{trade.underlying} {expiryCode(held.expiry)} · {legs.length} open leg{legs.length === 1 ? '' : 's'} · {held.units} units · spot {spot > 0 ? spot.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</span>
        </div>
      </footer>
    </>
  )
}
